import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  FileSignature,
  FileText,
  FileX,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LeaseFilters } from "../types/leases.types";
import LeasesFilters from "../components/LeasesFilters";
import { ErrorBoundary } from "@/common/components/ErrorBoundary";
import { PageHeader } from "@/components/shared/PageHeader";
import { cn } from "@/lib/utils";
import { DataTable } from "@/components/shared/DataTable/DataTable";
import { DataTablePagination } from "@/components/shared/DataTable/DataTablePagination";
import NoFound from "@/components/shared/NoFound";
import useLeases from "../hooks/useLeases";
import { getLeaseColumns } from "../components/columns";
import { LeaseWithRelations } from "../types/lease";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LeaseStatus } from "@/common/api/services/leases";

const LeasesContent = () => {
  const [isContainerMode, setIsContainerMode] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<LeaseFilters>({
    page: 0,
    size: 10,
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [leaseToDelete, setLeaseToDelete] = useState<string | null>(null);
  const { getLeases, deleteLease, updateLeaseStatus } = useLeases();
  const { data, isLoading: isLoadingLeases, isError } = getLeases(filters);

  const handleFiltersChange = (newFilters: LeaseFilters) => {
    setFilters(newFilters);
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  };

  const handleViewLease = (leaseUUID: string) => {
    const lease = data?.content.find(lease => lease.leaseUUID === leaseUUID);
    if (lease?.propertyUUID) {
      navigate(`/home/properties/${lease.propertyUUID}/leases/${leaseUUID}`);
    } else {
      console.error(`Lease ${leaseUUID} does not have propertyUUID`);
    }
  };

  const handleEditLease = (leaseUUID: string) => {
    const lease = data?.content.find(lease => lease.leaseUUID === leaseUUID);
    if (lease?.propertyUUID) {
      navigate(`/home/properties/${lease.propertyUUID}/leases/${leaseUUID}/edit`);
    } else {
      console.error(`Lease ${leaseUUID} does not have propertyUUID`);
    }
  };

  const handleAddLease = () => {
    navigate("create");
  };

  const handleOpenDeleteDialog = (leaseUUID: string) => {
    setLeaseToDelete(leaseUUID);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (leaseToDelete) {
      deleteLease(leaseToDelete);
      setIsDeleteDialogOpen(false);
      setLeaseToDelete(null);
    }
  };

  const handleStatusChange = (leaseUUID: string, status: LeaseStatus) => {
    updateLeaseStatus({ uuid: leaseUUID, status });
  };

  const columns = getLeaseColumns({
    t,
    onStatusChange: handleStatusChange,
    onView: handleViewLease,
    onEdit: handleEditLease,
    onDelete: handleOpenDeleteDialog,
    propertyUUID: undefined,
  });

  const leases = data?.content || [];
  const totalElements = data?.totalElements || 0;
  const totalPages = data?.totalPages || 0;

  return (
    <div
      className={cn("space-y-8 p-8", isContainerMode && "container mx-auto")}
    >
      <PageHeader
        icon={<FileSignature />}
        title={t("leases.title")}
        description={t("leases.description")}
        actions={
          <Button
            onClick={handleAddLease}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("leases.create.button")}
          </Button>
        }
        className="mb-8"
      />
      <LeasesFilters filters={filters} onChange={handleFiltersChange} />
      <DataTable
        columns={columns}
        data={leases as LeaseWithRelations[]}
        pagination={
          totalPages > 0 ? (
            <DataTablePagination
              table={
                {
                  getState: () => ({
                    pagination: {
                      pageIndex: filters.page || 0,
                      pageSize: filters.size || 10,
                    },
                  }),
                  getCanPreviousPage: () => (filters.page || 0) > 0,
                  getCanNextPage: () => (filters.page || 0) < totalPages - 1,
                  previousPage: () => handlePageChange((filters.page || 0) - 1),
                  nextPage: () => handlePageChange((filters.page || 0) + 1),
                  setPageIndex: (page: number) => handlePageChange(page),
                  setPageSize: (size: number) => { },
                  getPageCount: () => totalPages,
                } as any
              }
              totalElements={totalElements}
              totalPages={totalPages}
            />
          ) : null
        }
        noData={
          <NoFound
            titleTranslationKey="leases.noData.title"
            descriptionTranslationKey="leases.noData.description"
            hintTranslationKey="leases.noData.hint"
            actionTranslationKey="leases.actions.addLease"
            onAction={handleAddLease}
            icon={FileText}
            badgeIcon={FileX}
          />
        }
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("leases.delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("leases.delete.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const LeasesPage = () => {
  return (
    <ErrorBoundary>
      <LeasesContent />
    </ErrorBoundary>
  );
};

export default LeasesPage;
